import { SpanStore } from './span-store'
import { type BatcherOptions, type ExportResult, ExportResultCode } from './batcher'
import { decodeTime, hashString, retry, type RetryOptions } from '@w/utils'
import type {
  LogData,
  SpanData,
  TraceData,
  TelParent,
  ResourceUserInfo,
  TelResources,
} from './tel-types'
import { prepareForLog } from './prepare-for-log/prepare-for-log'
import type { PayloadLimits } from './prepare-for-log/prepare-for-log'
import { _createSpan } from './span'
import type { TelErrorData } from './tel-error-data'
import type {
  CumulativeMetricPoint,
  DeltaMetricPoint,
  GaugeMetricPoint,
  MetricPoint,
} from './tel-metrics-types'
import { type ClientStackResolver } from './client-error-groups'

// FIFO cleanup with retention priority: when limits are exceeded, prefer evicting
// successful data before failed pinned data. Automatic cleanup still removes
// completed trace / request resources. On client we preserve 3 last traces.

// these options should be adjusted based on the amount of concurrent users on server and browser memory
const DEFAULT_LOGS_STORAGE_LIMIT = 1_000
const DEFAULT_SPANS_STORAGE_LIMIT = 5_000
const STRUCTURAL_DEDUPE_TTL_MS = 5 * 60 * 1000 // 5 minutes
const STRUCTURAL_DEDUPE_MAX_SIZE = 100
const DEFAULT_METRICS_STORAGE_LIMIT = 1000
const DEFAULT_EVENTS_BUFFER_LIMIT = 200
const DEFAULT_METRICS_BUFFER_LIMIT = 100

/** Evict the oldest span that is neither open nor pinned; then unpinned; then closed; then the oldest. */
function _evictSpan(
  spans: SpanStore,
  activeIds: Set<string>,
  pinnedSpanIds: Set<string>,
): SpanData | undefined {
  return spans.evictOldest([
    (span) => !(activeIds.has(span.id) || pinnedSpanIds.has(span.id)),
    (span) => !pinnedSpanIds.has(span.id),
    (span) => !activeIds.has(span.id),
  ])
}

/** Evict oldest unpinned log first; pinned failure logs are last resort */
function _evictLog(
  logs: LogData[],
  pinnedLogIds: Set<string>,
): LogData | undefined {
  const index = logs.findIndex((log) => !pinnedLogIds.has(log.id))
  if (index !== -1) {
    const [removed] = logs.splice(index, 1)
    return removed
  }
  return logs.shift()
}

interface TelUserDataStore {
  get(): ResourceUserInfo
  set(data: ResourceUserInfo): void
  update(data: Partial<ResourceUserInfo>): void
  subscribe(callback: (data: ResourceUserInfo) => void): () => void
  clear(): void
}

export interface DropReport {
  spans: number
  logs: number
  metrics: number
  windowMs: number
}

export type TraceBoundaryEndHandler = (
  trace: TraceData,
  ctx?: HandlerContext,
) => unknown

export interface HandlerContext {
  /** One-based invocation attempt. */
  readonly attempt: number
  /** Aborts when delivery is cancelled. */
  readonly signal: AbortSignal
}

type TraceBoundaryReconstructor = (spanId: string) => TraceData

interface TraceBoundaryEndEvent {
  span: SpanData
  reconstructTrace: TraceBoundaryReconstructor
}

export interface TelErrorEvent {
  data: TelErrorData
  originalError: unknown
  userInfo: ResourceUserInfo
  /** Active error boundary first, then retained completed traces newest-first. */
  traces: TraceData[]
}

export interface TelStorageConfig {
  logsStorageLimit?: number
  spansStorageLimit?: number
  metricsStorageLimit?: number
  eventsBufferLimit?: number
  metricsBufferLimit?: number
  metricSeriesLimit?: number
  metricSeriesIdleIntervals?: number
  metricSketchMaxBins?: number
  metricsMode?: 'server' | 'serverless'
  perfMetricsAggregationInterval?: number
  errorMapper?: (error: unknown) => unknown
  stackResolver?: ClientStackResolver
  errorGroupScope?: string
  onDrop?: (report: DropReport) => void
  isSensitive?: (key: string, value: unknown) => boolean
  payloadLimits?: Partial<PayloadLimits>
  /** Completed traces retained for later contextual export. Default: 0. */
  recentTracesLimit?: number
}

export function createTelStorageCore(config: TelStorageConfig = {}) {
  const immediateHandlerContext: HandlerContext = Object.freeze({
    attempt: 1,
    signal: new AbortController().signal,
  })
  let {
    logsStorageLimit = DEFAULT_LOGS_STORAGE_LIMIT,
    spansStorageLimit = DEFAULT_SPANS_STORAGE_LIMIT,
    metricsStorageLimit = DEFAULT_METRICS_STORAGE_LIMIT,
    eventsBufferLimit = DEFAULT_EVENTS_BUFFER_LIMIT,
    metricsBufferLimit = DEFAULT_METRICS_BUFFER_LIMIT,
    metricsMode = 'server',
    errorMapper,
    stackResolver,
    errorGroupScope,
    onDrop,
    isSensitive,
    payloadLimits,
    recentTracesLimit = 0,
  } = config

  if (!Number.isInteger(recentTracesLimit) || recentTracesLimit < 0) {
    throw new Error(
      `Recent traces limit must be a non-negative integer, got ${recentTracesLimit}`,
    )
  }
  // Drop tracking with exponential backoff
  const dropCounts = { spans: 0, logs: 0, metrics: 0 }
  const completedRequestSnapshots = new Map<string, TraceData>()
  let firstDropTime: number | null = null
  let dropReportTimer: ReturnType<typeof setTimeout> | null = null
  let dropBackoffMs = 5000 // start at 5s
  const DROP_MAX_BACKOFF_MS = 60000 // cap at 1min

  function recordDrop(type: 'spans' | 'logs' | 'metrics') {
    if (!onDrop) return
    firstDropTime ??= Date.now()
    dropCounts[type]++
    scheduleDropReport()
  }

  function scheduleDropReport() {
    if (dropReportTimer || !onDrop) return
    dropReportTimer = setTimeout(() => {
      dropReportTimer = null
      const total = dropCounts.spans + dropCounts.logs + dropCounts.metrics
      if (total === 0) return

      const report: DropReport = {
        ...dropCounts,
        windowMs: Date.now() - (firstDropTime ?? Date.now()),
      }

      onDrop(report)

      // Reset counts but increase backoff
      dropCounts.spans = 0
      dropCounts.logs = 0
      dropCounts.metrics = 0
      firstDropTime = null
      dropBackoffMs = Math.min(dropBackoffMs * 2, DROP_MAX_BACKOFF_MS)
    }, dropBackoffMs)
  }

  function resetDropBackoff() {
    dropBackoffMs = 5000
  }

  let perfCallback: ((span: SpanData) => void) | null = null
  let handlersRegistered = false
  const flushCallbacks = new Set<() => Promise<void>>() // Store flush callbacks
  const pendingHandlerPromises = new Set<Promise<unknown>>()
  const finishedTraceIds: string[] = []
  const handlers: TelStorageEventHandlers = {
    logs: {},
    spans: {},
    errors: {},
    warnings: {},
    metrics: {},
  }

  function dispatchMetric(
    metricHandlers: TelStorageEventHandlers['metrics'],
    point: MetricPoint,
  ): boolean {
    if (point.streamMeta.kind === 'gauge') {
      if (!metricHandlers?.onCreateGauge) return false
      metricHandlers.onCreateGauge(point as GaugeMetricPoint, immediateHandlerContext)
      return true
    }
    if (point.streamMeta.temporality === 'delta') {
      if (!metricHandlers?.onCreateDelta) return false
      metricHandlers.onCreateDelta(point as DeltaMetricPoint, immediateHandlerContext)
      return true
    }
    if (!metricHandlers?.onCreateCumulative) return false
    metricHandlers.onCreateCumulative(point as CumulativeMetricPoint, immediateHandlerContext)
    return true
  }

  function trackHandlerPromise(result: unknown): Promise<unknown> | undefined {
    if (!(result instanceof Promise)) return undefined
    pendingHandlerPromises.add(result)
    void result
      .finally(() => {
        pendingHandlerPromises.delete(result)
      })
      .catch(() => {})
    return result
  }

  function scheduleBoundaryCleanup(
    boundary: CleanupBoundary,
    pending: Promise<unknown>[],
  ): void {
    if (pending.length === 0) {
      cleanupBoundary(boundary)
      return
    }
    const cleanup = Promise.allSettled(pending).then(() => {
      cleanupBoundary(boundary)
      return null
    })
    void trackHandlerPromise(cleanup)
  }

  // User data store
  let currentUserData: ResourceUserInfo = {}
  const userDataSubscribers = new Set<(data: ResourceUserInfo) => void>()

  const userDataStore: TelUserDataStore = {
    get() {
      return { ...currentUserData } // Return copy to prevent mutations
    },

    set(data: ResourceUserInfo) {
      currentUserData = { ...data }
      for (const callback of userDataSubscribers) callback(currentUserData)
    },

    update(data: Partial<ResourceUserInfo>) {
      currentUserData = { ...currentUserData, ...data }
      for (const callback of userDataSubscribers) callback(currentUserData)
    },

    subscribe(callback: (data: ResourceUserInfo) => void) {
      userDataSubscribers.add(callback)
      return () => {
        userDataSubscribers.delete(callback)
      }
    },

    clear() {
      currentUserData = {}
      for (const callback of userDataSubscribers) callback(currentUserData)
    },
  }

  const storage = {
    logs: [] as LogData[],
    spans: new SpanStore(),
    metrics: [] as MetricPoint[],
  }
  const activeSpanIds = new Set<string>()
  const pinnedSpanIds = new Set<string>()
  const pinnedLogIds = new Set<string>()
  type CleanupBoundary = { type: 'trace' | 'request'; id: string }
  const boundariesAwaitingHandlerReplay = new Map<string, CleanupBoundary>()
  // A completed request boundary nested under a still-open ancestor span (a
  // shared http/server-handler span this same storage has not ended yet)
  // must keep its spans in `storage.spans` -- the ancestor's own later
  // `_exportBoundary()` walks descendants structurally by `parentId`, and a
  // subtree removed here would be permanently unreachable from that export.
  // Cleanup is deferred to the moment the open ancestor itself ends.
  // Each retained boundary owns at most one deferred entry. Removal
  // of its record removes the entry; ancestor end/removal re-evaluates it.
  // Thus deferred metadata is bounded by retained spans, even while an
  // ancestor stays open across arbitrarily many completed requests.
  type DeferredCleanup = {
    boundary: CleanupBoundary
    span: SpanData
    ancestorId: string
    pending: Promise<unknown>[]
  }
  const deferredCleanupByBoundary = new Map<string, DeferredCleanup>()
  const pendingSpanFingerprints = new Map<string, string>()
  const spanRevisions = new Map<string, number>()
  // INV-1: a span revision/log of a boundary with a final sink delivery
  // reaches that sink in its final state at least once (live, eager or
  // final). All destination delivery filters and acknowledges in queue order.
  type LiveEntry = { span: SpanData; revision: number }
  type LiveWindow = { entries: Map<string, LiveEntry>; operation: Promise<unknown> }
  type SinkCursor = {
    liveWindow?: LiveWindow
    failedLive?: Map<string, LiveEntry>
    pending?: Promise<unknown>
    spanCursor: Map<string, number>
    logCursor: Set<string>
  }
  const deliveryBindings: Array<{
    handler: BoundTelExporter
    retry?: RetryOptions
    // Reference to the full cursor, not a snapshot, so a later
    // `wrapForSink` on a sink-sharing binding registered in a later
    // `setHandlers()` call is always read live.
    cursor: SinkCursor
    pending: Promise<unknown>
    queued: number
  }> = []
  /** Queued work of bindings a later `setHandlers()` replaced: it still drains, and `flush()` waits for it. */
  const retiredDeliveries = new Set<Promise<unknown>>()
  // Bindings sharing `sink` share one SinkCursor object so live sends and
  // eager/final delivery dedupe against the same destination state.
  const sinkCursors = new Map<string, SinkCursor>()
  const transportBatches = new Map<string, Set<{ flush: () => Promise<void>; close: () => void }>>()

  function getSinkCursor(sink: string): SinkCursor {
    let cursor = sinkCursors.get(sink)
    if (!cursor) {
      cursor = {
        spanCursor: new Map(),
        logCursor: new Set(),
      }
      sinkCursors.set(sink, cursor)
    }
    return cursor
  }

  // INV-4: a sink-cursor entry lives no longer than its storage record.
  // Called at every site that forgets a span/log (eviction, trace/request
  // cleanup) so records that belong to no boundary -- root-level logs,
  // foreign `insertEnded` spans, server-skipped client spans -- don't leak a
  // cursor entry forever (only boundary delivery's `clearDeliveryCursors`
  // releases boundary-owned entries).
  function forgetSinkCursorSpan(spanId: string): void {
    for (const { cursor } of deliveryBindings) {
      cursor.spanCursor.delete(spanId)
      cursor.failedLive?.delete(spanId)
    }
  }
  function forgetSinkCursorLog(logId: string): void {
    for (const { cursor } of deliveryBindings) cursor.logCursor.delete(logId)
  }

  /* oxlint-disable typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-return, typescript/no-unsafe-call, typescript/no-unsafe-argument -- generic wrapper forwards arbitrary handler signatures (span/log create/update/end) */
  // Live delivery captures payload and revision together; failed sends remain pending.
  function wrapSinkSpanHandler<T extends (span: SpanData, ...rest: any[]) => any>(
    fn: T | undefined,
    cursor: SinkCursor,
    batch?: (spans: SpanData[]) => unknown,
  ): T | undefined {
    if (!fn) return fn
    return ((span: SpanData, ...rest: any[]) => {
      const spanId = span.id
      const revision = spanRevisions.get(spanId) ?? 0
      const snapshot = structuredClone({ ...span, attributes: prepareForLog(span.attributes, { removeMark: true, isSensitive: () => false }) as SpanData['attributes'] })
      if (batch) {
        let window = cursor.liveWindow
        if (!window) {
          const entries = cursor.failedLive ?? new Map<string, LiveEntry>()
          delete cursor.failedLive
          // Defer collection until this destination reaches its next send window.
          const operation = enqueueDestination(cursor, async () => {
            await Promise.resolve()
            if (cursor.liveWindow?.entries === entries) delete cursor.liveWindow
            for (const [id, entry] of cursor.failedLive ?? []) {
              if ((entries.get(id)?.revision ?? 0) < entry.revision) entries.set(id, entry)
            }
            delete cursor.failedLive
            const pending = [...entries.values()].filter(entry =>
              (cursor.spanCursor.get(entry.span.id) ?? 0) < entry.revision)
            try {
              if (pending.length > 0) await batch(pending.map(entry => entry.span))
              for (const { span: sent, revision: sentRevision } of pending) {
                if (spanRevisions.has(sent.id)) {
                  cursor.spanCursor.set(sent.id, Math.max(cursor.spanCursor.get(sent.id) ?? 0, sentRevision))
                }
              }
            } catch {
              cursor.failedLive ??= new Map()
              for (const entry of pending) {
                if ((cursor.failedLive.get(entry.span.id)?.revision ?? 0) < entry.revision)
                  cursor.failedLive.set(entry.span.id, entry)
              }
            }
          })
          window = { entries, operation }
          cursor.liveWindow = window
          void trackHandlerPromise(operation)
        }
        window.entries.set(spanId, { span: snapshot, revision })
        return window.operation
      }
      const operation = enqueueDestination(cursor, async () => {
        if ((cursor.spanCursor.get(spanId) ?? 0) >= revision) return
        await fn(snapshot, ...rest)
        if (!spanRevisions.has(spanId)) return
        // INV-2: a cursor entry never lowers.
        if ((cursor.spanCursor.get(spanId) ?? 0) < revision) {
          cursor.spanCursor.set(spanId, revision)
        }
      }).catch(() => {})
      void trackHandlerPromise(operation)
      return operation
    }) as T
  }

  function wrapSinkLogHandler<T extends (log: LogData, ...rest: any[]) => any>(
    fn: T | undefined,
    cursor: SinkCursor,
  ): T | undefined {
    if (!fn) return fn
    return ((log: LogData, ...rest: any[]) => {
      const logId = log.id
      const snapshot = structuredClone({ ...log, attributes: prepareForLog(log.attributes, { removeMark: true, isSensitive: () => false }) as LogData['attributes'] })
      const operation = enqueueDestination(cursor, async () => {
        if (cursor.logCursor.has(logId)) return
        await fn(snapshot, ...rest)
        if (!storage.logs.some((record) => record.id === logId)) return
        cursor.logCursor.add(logId)
      }).catch(() => {})
      void trackHandlerPromise(operation)
      return operation
    }) as T
  }

  function wrapForSink(
    handler: BoundTelExporter,
    cursor: SinkCursor,
  ): BoundTelExporter {
    if (!handler.spans && !handler.logs) return handler
    return {
      ...handler,
      spans: handler.spans && {
        ...handler.spans,
        onCreate: wrapSinkSpanHandler(handler.spans.onCreate, cursor, handler.spans.onBatch),
        onUpdate: wrapSinkSpanHandler(handler.spans.onUpdate, cursor, handler.spans.onBatch),
        onEnd: wrapSinkSpanHandler(handler.spans.onEnd, cursor, handler.spans.onBatch),
      },
      logs: handler.logs && {
        ...handler.logs,
        onCreate: wrapSinkLogHandler(handler.logs.onCreate, cursor),
      },
    }
  }
  /* oxlint-enable typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-return, typescript/no-unsafe-call, typescript/no-unsafe-argument */

  function bumpSpanRevision(spanId: string): void {
    spanRevisions.set(spanId, (spanRevisions.get(spanId) ?? 0) + 1)
  }

  function getCompletedBoundary(span: SpanData): CleanupBoundary | undefined {
    const locallyOwnedTrace = Boolean(
      span.traceId && storage.spans.get(span.traceId)?.traceId === span.traceId,
    )
    if (
      span.traceId &&
      locallyOwnedTrace &&
      !hasUnfinished('traceId', span.traceId)
    ) {
      return { type: 'trace', id: span.traceId }
    }

    if (
      span.requestId &&
      !locallyOwnedTrace &&
      storage.spans.get(span.requestId)?.requestId === span.requestId &&
      !hasUnfinished('requestId', span.requestId)
    ) {
      return { type: 'request', id: span.requestId }
    }

    return undefined
  }

  function hasUnfinished(field: 'traceId' | 'requestId', id: string): boolean {
    return storage.spans.hasUnfinished(field, id)
  }

  function cleanupBoundary(boundary: CleanupBoundary): void {
    if (boundary.type === 'request') {
      storageObj.cleanupRequest(boundary.id)
      return
    }
    finishedTraceIds.push(boundary.id)
    while (finishedTraceIds.length > recentTracesLimit) {
      const oldestTraceId = finishedTraceIds.shift()
      if (oldestTraceId) storageObj.cleanupTrace(oldestTraceId)
    }
  }

  function findSpanById(id: string): SpanData | undefined {
    return storage.spans.get(id)
  }

  function findOpenAncestorId(span: SpanData): string | undefined {
    let current = span
    const visited = new Set<string>()
    while (current.parentId && !visited.has(current.id)) {
      visited.add(current.id)
      const parent = findSpanById(current.parentId)
      if (!parent) return undefined
      const parentEnded =
        (parent.status === 'ok' ||
          parent.status === 'error' ||
          parent.status === 'warning') &&
        typeof parent.duration === 'number'
      if (!parentEnded) return parent.id
      current = parent
    }
    return undefined
  }

  function deferOrCleanupBoundary(
    boundary: CleanupBoundary,
    span: SpanData,
    pending: Promise<unknown>[],
  ): void {
    const openAncestorId = findOpenAncestorId(span)
    if (!openAncestorId) {
      scheduleBoundaryCleanup(boundary, pending)
      return
    }
    deferredCleanupByBoundary.set(boundary.id, {
      boundary, span, ancestorId: openAncestorId, pending,
    })
  }

  // Runs when `endedSpanId` itself ends: any boundary that was waiting on it
  // as its nearest open ancestor is now re-evaluated -- cleaned up if nothing
  // else above it is still open, or re-parked under the next open ancestor.
  function drainDeferredCleanup(endedSpanId: string): void {
    const deferred = [...deferredCleanupByBoundary.values()]
      .filter((entry) => entry.ancestorId === endedSpanId)
    for (const { boundary, span, pending } of deferred) {
      deferredCleanupByBoundary.delete(boundary.id)
      deferOrCleanupBoundary(boundary, span, pending)
    }
  }

  function createBoundaryEndEvent(
    boundary: CleanupBoundary,
  ): TraceBoundaryEndEvent | undefined {
    const boundarySpan = storage.spans.get(boundary.id)
    if (!boundarySpan) return undefined
    return {
      span: boundarySpan,
      reconstructTrace: createTraceReconstructor(
        boundarySpan,
        storage.spans,
        storage.logs,
      ),
    }
  }

  function retainCompletedRequest(
    boundary: CleanupBoundary,
    event: TraceBoundaryEndEvent | undefined,
  ): void {
    if (boundary.type !== 'request' || !event) return
    const trace = event.reconstructTrace(event.span.id)
    if (!trace) return
    completedRequestSnapshots.set(boundary.id, trace)
    while (completedRequestSnapshots.size > eventsBufferLimit) {
      const oldestId = completedRequestSnapshots.keys().next().value
      if (typeof oldestId === 'string')
        completedRequestSnapshots.delete(oldestId)
      else break
    }
  }

  function flattenTraceData(trace: TraceData): {
    spans: SpanData[]
    logs: LogData[]
  } {
    const spans: SpanData[] = []
    const logs: LogData[] = []
    const visit = (node: TraceData) => {
      const { children, logs: nodeLogs, ...span } = node
      spans.push(span)
      logs.push(...nodeLogs)
      for (const child of children) visit(child)
    }
    visit(trace)
    return { spans, logs }
  }

  function snapshotRevisions(traces: TraceData[]): ReadonlyMap<string, number> {
    const revisions = new Map<string, number>()
    const visit = (trace: TraceData) => {
      revisions.set(trace.id, spanRevisions.get(trace.id) ?? 0)
      for (const child of trace.children) visit(child)
    }
    for (const trace of traces) visit(trace)
    return revisions
  }

  // INV-2: a shared cursor's span entry only moves to a higher revision
  // (live resolve and eager ack alike never lower it).
  function acknowledgeTrace(
    delivery: (typeof deliveryBindings)[number],
    trace: TraceData,
    revisions: ReadonlyMap<string, number>,
  ): void {
    const snapshot = flattenTraceData(trace)
    for (const span of snapshot.spans) {
      if (!spanRevisions.has(span.id)) continue
      const acked = revisions.get(span.id) ?? 0
      if ((delivery.cursor.spanCursor.get(span.id) ?? 0) < acked) {
        delivery.cursor.spanCursor.set(span.id, acked)
      }
      if ((delivery.cursor.failedLive?.get(span.id)?.revision ?? Infinity) <= acked)
        delivery.cursor.failedLive?.delete(span.id)
    }
    for (const log of snapshot.logs) {
      if (storage.logs.some((record) => record.id === log.id))
        delivery.cursor.logCursor.add(log.id)
    }
  }

  function clearDeliveryCursors(
    delivery: (typeof deliveryBindings)[number],
    snapshot: ReturnType<typeof flattenTraceData>,
  ): void {
    if (delivery.handler.sink !== undefined) return
    for (const span of snapshot.spans) delivery.cursor.spanCursor.delete(span.id)
    for (const log of snapshot.logs) delivery.cursor.logCursor.delete(log.id)
  }

  function pendingTrace(
    trace: TraceData,
    delivery: (typeof deliveryBindings)[number],
    revisions: ReadonlyMap<string, number>,
  ): TraceData | null {
    const children = trace.children
      .map((child) => pendingTrace(child, delivery, revisions))
      .filter((child): child is TraceData => child !== null)
    const logs = trace.logs.filter((log) => !delivery.cursor.logCursor.has(log.id))
    const changed =
      (delivery.cursor.spanCursor.get(trace.id) ?? 0) <
      (revisions.get(trace.id) ?? 0)
    if (!changed && children.length === 0 && logs.length === 0) return null
    return { ...trace, children, logs }
  }

  function scheduleBoundaryDelivery(
    boundary: CleanupBoundary,
    trace: TraceData,
  ): Promise<unknown>[] {
    const snapshot = flattenTraceData(trace)
    const revisions = snapshotRevisions([trace])
    const participating = deliveryBindings.filter((delivery) =>
      Boolean(
        boundary.type === 'trace'
          ? delivery.handler.traces?.onEnd
          : delivery.handler.requests?.onEnd,
      ) && (delivery.handler.acceptsBoundary?.(trace) ?? true),
    )
    // Cursor entries are shared across every binding on the same `sink`; only
    // release them once every participating binding settled this boundary.
    const cursorSettleCounts = new Map<Map<string, number>, number>()
    for (const delivery of participating) {
      cursorSettleCounts.set(
        delivery.cursor.spanCursor,
        (cursorSettleCounts.get(delivery.cursor.spanCursor) ?? 0) + 1,
      )
    }
    return participating.map((delivery) => {
      const callback =
        boundary.type === 'trace'
          ? delivery.handler.traces?.onEnd
          : delivery.handler.requests?.onEnd
      const run = async () => {
        try {
          const pending = pendingTrace(trace, delivery, revisions)
          if (pending && callback) {
            await retry(
              (attempt) =>
                callback(pending, {
                  attempt,
                  signal:
                    delivery.retry?.signal ?? immediateHandlerContext.signal,
                }),
              delivery.retry,
            )
            acknowledgeTrace(delivery, pending, revisions)
          }
          return null
        } finally {
          const remaining = (cursorSettleCounts.get(delivery.cursor.spanCursor) ?? 1) - 1
          cursorSettleCounts.set(delivery.cursor.spanCursor, remaining)
          if (remaining <= 0) clearDeliveryCursors(delivery, snapshot)
        }
      }
      const operation = enqueueDestination(delivery.cursor, run)
      delivery.queued++
      void operation
        .finally(() => {
          delivery.queued--
        })
        .catch(() => null)
      delivery.pending = operation.catch(() => null)
      return operation
    })
  }

  function scheduleErrorDelivery(
    event: TelErrorEvent,
    revisions: ReadonlyMap<string, number>,
  ): void {
    for (const delivery of deliveryBindings) {
      const callback = delivery.handler.errors?.onCreate
      if (!callback) continue
      const run = async () => {
        const traces = event.traces
          .map((item) => pendingTrace(item, delivery, revisions))
          .filter((item): item is TraceData => item !== null)
        await retry(
          (attempt) =>
            callback(
              { ...event, traces },
              {
                attempt,
                signal:
                  delivery.retry?.signal ?? immediateHandlerContext.signal,
              },
            ),
          delivery.retry,
        )
        // Cursor is only ever written here, after a successful delivery --
        // a failed/exhausted attempt writes nothing, so there is nothing to
        // roll back on failure. A rollback that clears the shared cursor on
        // failure would also erase entries other bindings (live sends,
        // other eager acks) already wrote for the same span/log.
        // Snapshot revisions are captured with the event, before buffering,
        // queueing or awaiting transport. Later mutations remain pending.
        for (const item of traces) acknowledgeTrace(delivery, item, revisions)
        return null
      }
      const operation = enqueueDestination(delivery.cursor, run)
      delivery.queued++
      void operation
        .finally(() => {
          delivery.queued--
        })
        .catch(() => null)
      delivery.pending = operation.catch(() => null)
      void trackHandlerPromise(operation)
    }
  }

  function enqueueDestination(cursor: SinkCursor, run: () => Promise<unknown>): Promise<unknown> {
    const operation = cursor.pending ? cursor.pending.then(run) : run()
    const settled = operation.catch(() => null)
    cursor.pending = settled
    void settled.then(() => {
      if (cursor.pending === settled) delete cursor.pending
      return null
    })
    return operation
  }

  function getErrorContext(trace: TraceData | null): TraceData[] {
    if (!trace || trace.origin !== 'client') return []
    return finishedTraceIds
      .toReversed()
      .filter((traceId) => traceId !== trace.traceId)
      .slice(0, recentTracesLimit)
      .map((traceId) => reconstructTrace(traceId, storage.spans, storage.logs, true))
      .filter((item): item is TraceData => item !== null)
  }

  function reconstructErrorBoundary(spanId: string): TraceData | null {
    const span = storage.spans.get(spanId)
    if (!span) return null
    const traceOwned = Boolean(span.traceId && storage.spans.get(span.traceId)?.traceId === span.traceId)
    const boundaryId = traceOwned ? span.traceId : span.requestId
    // INV-6: an explicit trace/request boundary owns diagnostics even while its causal caller is open.
    return reconstructTrace(boundaryId ?? spanId, storage.spans, storage.logs, !boundaryId)
  }

  // Track seen errors across their cause-chain to avoid duplicate reporting
  const seenErrors = new WeakSet<object>()
  // WeakSet ensures no memory leaks: entries are garbage-collected once errors are unreachable

  // Structural dedupe cache: map signature -> last seen timestamp
  const structuralErrorCache = new Map<string, number>()

  function pruneStructuralCache() {
    if (structuralErrorCache.size === 0) return
    const now = Date.now()
    // Remove expired
    for (const [sig, ts] of structuralErrorCache) {
      if (now - ts > STRUCTURAL_DEDUPE_TTL_MS) structuralErrorCache.delete(sig)
    }
    // Enforce max size by evicting oldest
    if (structuralErrorCache.size > STRUCTURAL_DEDUPE_MAX_SIZE) {
      const entries = Array.from(structuralErrorCache.entries()).toSorted(
        (a, b) => a[1] - b[1],
      )
      const excess = structuralErrorCache.size - STRUCTURAL_DEDUPE_MAX_SIZE
      for (let i = 0; i < excess; i++)
        structuralErrorCache.delete(entries[i][0])
    }
  }

  // Build a stable signature for an error-like object
  function buildErrorSignature(obj: unknown): string {
    if (obj && typeof obj === 'object') {
      // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-assignment -- dynamic error-like object inspection, shape unknown at compile time
      const anyObj = obj as any
      // oxlint-disable-next-line typescript/no-unsafe-member-access, typescript/prefer-nullish-coalescing -- dynamic error-like object inspection, shape unknown at compile time; empty name must fall through
      const name = String(anyObj.name || anyObj.constructor?.name || 'Error')
      // oxlint-disable-next-line typescript/no-unsafe-member-access -- dynamic error-like object inspection, shape unknown at compile time
      const message = String(anyObj.message ?? '')
      // oxlint-disable-next-line typescript/no-unsafe-member-access -- dynamic error-like object inspection, shape unknown at compile time
      const stack = String(anyObj.stack ?? '')
      return hashString(`${name}|${message}|${stack}`)
    }
    try {
      return hashString(`${typeof obj}|${String(obj)}`)
    } catch {
      return 'unknown'
    }
  }

  const allEventsBuffer = {
    logs: {
      create: [] as LogData[],
    },
    span: {
      create: [] as SpanData[],
      update: [] as Partial<SpanData>[],
      end: [] as Partial<SpanData>[],
    },
    trace: {
      end: [] as TraceBoundaryEndEvent[],
    },
    requests: {
      end: [] as TraceBoundaryEndEvent[],
    },
    errors: {
      create: [] as Array<{ event: TelErrorEvent; revisions: ReadonlyMap<string, number> }>,
    },
    warnings: {
      create: [] as Array<{ event: TelErrorEvent }>,
    },
    metrics: {
      create: [] as MetricPoint[],
    },
  }

  const storageObj = {
    userDataStore,
    logs: {
      create(data: LogData) {
        data.attributes = prepareForLog(data.attributes, { removeMark: true, isSensitive: () => false }) as LogData['attributes']
        data.body = prepareForLog(data.body, {
          removeMark: true,
          isSensitive,
          ...payloadLimits,
        })
        if (data.parentId && pinnedSpanIds.has(data.parentId)) {
          pinnedLogIds.add(data.id)
        }
        storage.logs.push(data)
        if (storage.logs.length > logsStorageLimit) {
          const removed = _evictLog(storage.logs, pinnedLogIds)
          if (removed) {
            pinnedLogIds.delete(removed.id)
            forgetSinkCursorLog(removed.id)
          }
          recordDrop('logs')
        }

        if (handlersRegistered) {
          handlers.logs?.onCreate?.(data, immediateHandlerContext)
        } else if (allEventsBuffer.logs.create.length < eventsBufferLimit) {
          allEventsBuffer.logs.create.push(data)
        }
      },
      insert(data: LogData) {
        data.attributes = prepareForLog(data.attributes, { removeMark: true, isSensitive: () => false }) as LogData['attributes']
        if (data.parentId && pinnedSpanIds.has(data.parentId)) {
          pinnedLogIds.add(data.id)
        }
        storage.logs.push(data)
        if (storage.logs.length > logsStorageLimit) {
          const removed = _evictLog(storage.logs, pinnedLogIds)
          if (removed) {
            pinnedLogIds.delete(removed.id)
            forgetSinkCursorLog(removed.id)
          }
          recordDrop('logs')
        }

        if (handlersRegistered)
          handlers.logs?.onCreate?.(data, immediateHandlerContext)
        else if (allEventsBuffer.logs.create.length < eventsBufferLimit)
          allEventsBuffer.logs.create.push(data)
      },
    },
    metrics: {
      mode() {
        return metricsMode
      },
      create(data: MetricPoint) {
        storage.metrics.push(data)
        if (storage.metrics.length > metricsStorageLimit) {
          storage.metrics.shift()
          recordDrop('metrics')
        }

        if (handlersRegistered) {
          dispatchMetric(handlers.metrics, data)
        } else if (allEventsBuffer.metrics.create.length < metricsBufferLimit) {
          allEventsBuffer.metrics.create.push(data)
        } else {
          recordDrop('metrics')
        }
      },
      subscriptions() {
        if (!handlersRegistered) return { delta: true, cumulative: true, gauge: true }
        return {
          delta: Boolean(handlers.metrics?.onCreateDelta),
          cumulative: Boolean(handlers.metrics?.onCreateCumulative),
          gauge: Boolean(handlers.metrics?.onCreateGauge),
        }
      },
      reportDrop() {
        recordDrop('metrics')
      },
    },
    spans: {
      create(
        data: SpanData & { alreadyMasked?: boolean },
        parent?: TelParent,
        callerUserInfo?: ResourceUserInfo,
      ) {
        // Use caller-provided userInfo (from tel-level copy-on-write), fallback to store for route spans
        const isRouteSpan = data.target === 'router'
        const freshUserData =
          callerUserInfo ?? (isRouteSpan ? userDataStore.get() : {})
        const { alreadyMasked, ...spanData } = data

        const span = _createSpan(
          {
            ...spanData,
            parentId: spanData.parentId ?? parent?.id,
            requestId: spanData.requestId ?? parent?.requestId,
            sessionId: spanData.sessionId ?? parent?.sessionId,
            traceId: spanData.traceId ?? parent?.traceId,
            traceFlags: spanData.traceFlags ?? parent?.traceFlags,
            traceState: spanData.traceState ?? parent?.traceState,
            baggage: spanData.baggage ?? parent?.baggage,
            otel:
              spanData.otel ??
              (parent?.otel
                ? {
                    traceId: parent.otel.traceId,
                  }
                : undefined),
            performanceMetricId:
              spanData.performanceMetricId ?? parent?.performanceMetricId,
            // Apply user data: from caller (copy-on-write tel) or store (route spans only)
            // oxlint-disable-next-line typescript/prefer-nullish-coalescing -- falsy callerUserInfo must fall through to isRouteSpan boolean check
            ...((callerUserInfo || isRouteSpan) && {
              userId: spanData.userId ?? freshUserData.userId,
              companyId: spanData.companyId ?? freshUserData.companyId,
            }),
          },
          {
            // oxlint-disable-next-line typescript/unbound-method -- plain object method, no this binding needed
            end: storageObj.spans.end,
            // oxlint-disable-next-line typescript/unbound-method -- plain object method, no this binding needed
            update: storageObj.spans.update,
            errors: storageObj.spans.errors,
          },
          { alreadyMasked, isSensitive, payloadLimits },
        )

        const pendingFingerprint = pendingSpanFingerprints.get(span.data.id)
        if (pendingFingerprint && !span.data.fingerprint) {
          span.data.fingerprint = pendingFingerprint
        }
        span.data.attributes = prepareForLog(span.data.attributes, { removeMark: true, isSensitive: () => false }) as SpanData['attributes']
        storage.spans.add(span.data)
        bumpSpanRevision(span.data.id)
        activeSpanIds.add(span.data.id)
        if (storage.spans.size > spansStorageLimit) {
          const removed = _evictSpan(
            storage.spans,
            activeSpanIds,
            pinnedSpanIds,
          )
          if (removed) {
            spanRevisions.delete(removed.id)
            deferredCleanupByBoundary.delete(removed.id)
            pinnedSpanIds.delete(removed.id)
            forgetSinkCursorSpan(removed.id)
            for (const log of storage.logs) {
              if (log.parentId === removed.id) pinnedLogIds.delete(log.id)
            }
            // An evicted open ancestor can no longer be waited on --
            // re-evaluate everything deferred under it now, so it doesn't
            // outlive the ancestor record it depends on (see
            // `drainDeferredCleanup`).
            drainDeferredCleanup(removed.id)
          }
          recordDrop('spans')
        }

        if (handlersRegistered)
          handlers.spans?.onCreate?.(span.data, immediateHandlerContext) // it can't be just data because it is not handled by prepareForLog. in other methods it's not applicable because they should be called from Span instances with proper data handling.
        else if (allEventsBuffer.span.create.length < eventsBufferLimit)
          allEventsBuffer.span.create.push(span.data)

        return span
      },
      insertEnded(data: SpanData) {
        storage.spans.add(data)
        bumpSpanRevision(data.id)
        if (storage.spans.size > spansStorageLimit) {
          const removed = _evictSpan(
            storage.spans,
            activeSpanIds,
            pinnedSpanIds,
          )
          if (removed) {
            spanRevisions.delete(removed.id)
            deferredCleanupByBoundary.delete(removed.id)
            pinnedSpanIds.delete(removed.id)
            forgetSinkCursorSpan(removed.id)
            for (const log of storage.logs) {
              if (log.parentId === removed.id) pinnedLogIds.delete(log.id)
            }
            drainDeferredCleanup(removed.id)
          }
          recordDrop('spans')
        }

        // Foreign ended spans are events only, never locally owned boundaries.
        if (handlersRegistered) {
          handlers.spans?.onCreate?.(data, immediateHandlerContext)
          handlers.spans?.onEnd?.(data, data, immediateHandlerContext)
        } else {
          if (allEventsBuffer.span.create.length < eventsBufferLimit)
            allEventsBuffer.span.create.push(data)
          if (allEventsBuffer.span.end.length < eventsBufferLimit)
            allEventsBuffer.span.end.push(data)
        }
      },
      update(data: Partial<SpanData> & { id: string }) {
        const span = storage.spans.get(data.id)
        // Evicted by the storage cap, already reported through onDrop.
        if (!span) return
        const update = { ...data }
        if (update.attributes) {
          span.attributes ??= {}
          Object.assign(span.attributes, prepareForLog(update.attributes, { removeMark: true, isSensitive: () => false }))
          delete update.attributes
        }
        Object.assign(span, update)
        storage.spans.touch(span)
        // One revision per applied mutation, regardless of whether
        // `span.ts` already wrote the same values onto the shared `this.data`
        // object before calling storage -- this call IS the mutation.
        bumpSpanRevision(span.id)
        const pendingFingerprint = pendingSpanFingerprints.get(span.id)
        if (pendingFingerprint && !span.fingerprint) {
          span.fingerprint = pendingFingerprint
        }

        if (handlersRegistered)
          handlers.spans?.onUpdate?.(span, data, immediateHandlerContext)
        else if (allEventsBuffer.span.update.length < eventsBufferLimit)
          allEventsBuffer.span.update.push(data)
      },
      end(data: Partial<SpanData> & { id: string }) {
        const span = storage.spans.get(data.id)
        // Evicted by the storage cap, already reported through onDrop.
        if (!span) return
        activeSpanIds.delete(data.id)
        // merge attributes
        const endUpdate = { ...data }
        if (endUpdate.attributes) {
          span.attributes ??= {}
          Object.assign(span.attributes, prepareForLog(endUpdate.attributes, { removeMark: true, isSensitive: () => false }))
          delete endUpdate.attributes
        }

        Object.assign(span, endUpdate)
        storage.spans.touch(span)
        // One revision per applied mutation. `end()` always ends the
        // span (duration/status), so it always owns a revision, even when
        // `span.ts` pre-mutated the shared `this.data` object to the same
        // values before calling storage.
        bumpSpanRevision(span.id)

        // oxlint-disable-next-line eqeqeq -- intentional null+undefined check
        if (span.performanceMetricId && span.duration != null) {
          perfCallback?.(span)
        }

        if (handlersRegistered) {
          handlers.spans?.onEnd?.(span, data, immediateHandlerContext)
          const boundary = getCompletedBoundary(span)
          if (boundary) {
            const event = createBoundaryEndEvent(boundary)
            retainCompletedRequest(boundary, event)
            const trace = event?.reconstructTrace(event.span.id)
            const pending: Promise<unknown>[] = trace
              ? scheduleBoundaryDelivery(boundary, trace)
              : []
            deferOrCleanupBoundary(boundary, span, pending)
          }
          drainDeferredCleanup(span.id)
        } else if (allEventsBuffer.span.end.length < eventsBufferLimit) {
          allEventsBuffer.span.end.push(data)
          const boundary = getCompletedBoundary(span)
          if (boundary) {
            const event = createBoundaryEndEvent(boundary)
            retainCompletedRequest(boundary, event)
            if (event) {
              event.reconstructTrace(event.span.id)
              const buffer =
                boundary.type === 'trace'
                  ? allEventsBuffer.trace.end
                  : allEventsBuffer.requests.end
              if (buffer.length < eventsBufferLimit) buffer.push(event)
            }
            boundariesAwaitingHandlerReplay.set(
              `${boundary.type}:${boundary.id}`,
              boundary,
            )
          }
        }
      },
      errors: {
        create(
          originalError: unknown,
          data: TelErrorData,
          userInfo: ResourceUserInfo = {},
          opts?: { dedupeBySpan?: boolean },
        ) {
          if (!originalError) return

          // Apply error mapper if configured
          const mappedError = errorMapper
            ? errorMapper(originalError)
            : originalError

          // Use provided userInfo (from tel-level copy-on-write), fallback to store
          const freshUserData = userDataStore.get()
          const finalUserInfo = { ...freshUserData, ...userInfo }

          // Deduplicate by traversing the cause-chain (cycle-safe) and checking WeakSet
          const chain: object[] = []
          const visited = new WeakSet<object>()
          let current: unknown = mappedError
          let depth = 0
          const MAX_DEPTH = 20
          while (current && typeof current === 'object' && depth < MAX_DEPTH) {
            if (visited.has(current)) break
            visited.add(current)
            chain.push(current)
            // oxlint-disable-next-line typescript/no-unsafe-assignment -- error cause chain, shape unknown
            const next =
              // oxlint-disable-next-line typescript/no-explicit-any, typescript/no-unsafe-member-access -- error cause chain, shape unknown
              'cause' in (current as any) ? (current as any).cause : undefined
            if (!next || typeof next !== 'object') break
            current = next
            depth++
          }

          // INV-6: callers choose error identity; span names carry no policy.
          const dedupeBySpan = opts?.dedupeBySpan === true
          if (!dedupeBySpan && chain.some((e) => seenErrors.has(e)))
            // If any object in the chain was already seen, skip creating a duplicate error
            return

          // Structural dedupe: skip if same signature seen within TTL for any cause
          let chainSignatures: string[] = []
          let now = Date.now()
          pruneStructuralCache()
          now = Date.now()
          chainSignatures = chain.map((e) => dedupeBySpan ? `${buildErrorSignature(e)}:${data.spanId}` : buildErrorSignature(e))
          const withinTtl = chainSignatures.some((sig) => {
            const ts = structuralErrorCache.get(sig)
            return ts !== undefined && now - ts <= STRUCTURAL_DEDUPE_TTL_MS
          })
          if (withinTtl) return

          if (!data) return

          if (data.spanId) {
            // Content mutation owns the revision; error creation does
            // not mutate the span's stored content, so it never bumps. The
            // span's own `end()` (called synchronously right after, for an
            // ending span) is what carries this error into a new revision.
            pinSpanChain(
              data.spanId,
              storage.spans,
              storage.logs,
              pinnedSpanIds,
              pinnedLogIds,
            )
          }
          const trace = reconstructErrorBoundary(data.spanId ?? '')
          const event: TelErrorEvent = {
            data,
            originalError: mappedError,
            userInfo: finalUserInfo,
            traces: [...(trace ? [trace] : []), ...getErrorContext(trace)],
          }

          if (data.level === 'warning') {
            if (handlersRegistered)
              void handlers.warnings?.onCreate?.(event, immediateHandlerContext)
            else if (allEventsBuffer.warnings.create.length < eventsBufferLimit)
              allEventsBuffer.warnings.create.push({ event })
          } else if (handlersRegistered) {
            scheduleErrorDelivery(event, snapshotRevisions(event.traces))
          } else if (allEventsBuffer.errors.create.length < eventsBufferLimit) {
            allEventsBuffer.errors.create.push({ event, revisions: snapshotRevisions(event.traces) })
          }

          // Mark the entire chain as seen to prevent future duplicates
          for (const e of chain) seenErrors.add(e)

          // Update structural dedupe timestamps for all signatures in the chain
          for (const sig of chainSignatures) structuralErrorCache.set(sig, now)
        },
      },
    },

    hasUnfinishedSpans(traceId: string): boolean {
      if (!traceId) return false

      // Find spans belonging to this trace
      return hasUnfinished('traceId', traceId)
    },

    cleanupTrace(traceId: string) {
      if (!traceId) return

      const removedSpanIds: string[] = []
      for (const span of storage.spans.inTrace(traceId)) {
        spanRevisions.delete(span.id)
        deferredCleanupByBoundary.delete(span.id)
        pinnedSpanIds.delete(span.id)
        forgetSinkCursorSpan(span.id)
        removedSpanIds.push(span.id)
        storage.spans.delete(span.id)
      }
      for (const log of storage.logs) {
        if (log.traceId === traceId) {
          pinnedLogIds.delete(log.id)
          forgetSinkCursorLog(log.id)
        }
      }

      storage.logs = storage.logs.filter((log) => log.traceId !== traceId)
      boundariesAwaitingHandlerReplay.delete(`trace:${traceId}`)
      const finishedIndex = finishedTraceIds.indexOf(traceId)
      if (finishedIndex !== -1) finishedTraceIds.splice(finishedIndex, 1)
      // None of these removed spans can serve as an open ancestor
      // any more; drain whatever was deferred under any of them.
      for (const id of removedSpanIds) drainDeferredCleanup(id)
    },

    cleanupRequest(requestId: string) {
      if (!requestId) return

      const removedSpanIds: string[] = []
      for (const span of storage.spans.inRequest(requestId)) {
        spanRevisions.delete(span.id)
        deferredCleanupByBoundary.delete(span.id)
        pinnedSpanIds.delete(span.id)
        forgetSinkCursorSpan(span.id)
        removedSpanIds.push(span.id)
        storage.spans.delete(span.id)
      }
      for (const log of storage.logs) {
        if (log.requestId === requestId) {
          pinnedLogIds.delete(log.id)
          forgetSinkCursorLog(log.id)
        }
      }

      storage.logs = storage.logs.filter((log) => log.requestId !== requestId)
      boundariesAwaitingHandlerReplay.delete(`request:${requestId}`)
      for (const id of removedSpanIds) drainDeferredCleanup(id)
    },

    updateConfig(newConfig: TelStorageConfig) {
      const {
        logsStorageLimit: newLogsStorageLimit,
        spansStorageLimit: newSpansStorageLimit,
        metricsStorageLimit: newMetricsStorageLimit,
        eventsBufferLimit: newEventsBufferLimit,
        metricsBufferLimit: newMetricsBufferLimit,
        errorMapper: newErrorMapper,
        stackResolver: newStackResolver,
        payloadLimits: newPayloadLimits,
        recentTracesLimit: newRecentTracesLimit,
      } = newConfig

      if (newLogsStorageLimit !== undefined) {
        logsStorageLimit = newLogsStorageLimit
      }
      if (newSpansStorageLimit !== undefined) {
        spansStorageLimit = newSpansStorageLimit
      }
      if (newMetricsStorageLimit !== undefined) {
        metricsStorageLimit = newMetricsStorageLimit
      }
      if (newEventsBufferLimit !== undefined) {
        eventsBufferLimit = newEventsBufferLimit
      }
      if (newMetricsBufferLimit !== undefined) {
        metricsBufferLimit = newMetricsBufferLimit
      }
      if (newErrorMapper !== undefined) {
        errorMapper = newErrorMapper
      }
      if (newStackResolver !== undefined) {
        stackResolver = newStackResolver
      }
      if (newPayloadLimits !== undefined) {
        payloadLimits = newPayloadLimits
      }
      if (newRecentTracesLimit !== undefined) {
        if (
          !Number.isInteger(newRecentTracesLimit) ||
          newRecentTracesLimit < 0
        ) {
          throw new Error(
            `Recent traces limit must be a non-negative integer, got ${newRecentTracesLimit}`,
          )
        }
        recentTracesLimit = newRecentTracesLimit
        while (finishedTraceIds.length > recentTracesLimit) {
          const oldestTraceId = finishedTraceIds.shift()
          if (oldestTraceId) storageObj.cleanupTrace(oldestTraceId)
        }
      }
    },

    setHandlers(
      ...inputHandlers: Array<TelStorageEventHandlers | BoundTelExporter>
    ) {
      // Sink bindings share one cursor and get their live span/log handlers
      // wrapped to advance that cursor on resolve. Buffered replay below runs
      // through the same wrapped, merged handlers.
      // INV-3: a binding with no `sink` always gets its own private,
      // never-shared cursor (below), so it always receives complete
      // boundaries regardless of what any sink binding has acked.
      function getSink(
        handler: TelStorageEventHandlers | BoundTelExporter,
      ): string | undefined {
        return 'sink' in handler ? handler.sink : undefined
      }
      function getRetry(
        handler: TelStorageEventHandlers | BoundTelExporter,
      ): RetryOptions | undefined {
        return 'retry' in handler ? handler.retry : undefined
      }

      const resolvedHandlers = inputHandlers.map((handler) => {
        const sink = getSink(handler)
        if (sink === undefined) return handler
        return wrapForSink(handler, getSinkCursor(sink))
      })

      // setHandlers() replaces the bound exporters, as `Object.assign(handlers, …)` below does for live handlers:
      // a replaced binding gets no later delivery (set-handlers-replace.test.ts).
      for (const retired of deliveryBindings.splice(0)) {
        if (retired.queued === 0) continue
        const pending: Promise<unknown> = retired.pending.finally(() => { retiredDeliveries.delete(pending) }).catch(() => null)
        retiredDeliveries.add(pending)
      }
      for (const handler of resolvedHandlers) {
        const sink = getSink(handler)
        const cursor: SinkCursor = sink === undefined
          ? {
              spanCursor: new Map(),
              logCursor: new Set(),
            }
          : getSinkCursor(sink)
        deliveryBindings.push({
          handler,
          retry: getRetry(handler),
          cursor,
          pending: Promise.resolve(),
          queued: 0,
        })
      }
      /* oxlint-disable typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-return -- generic handler merger requires any for type-safe variadic dispatch */
      function mergeHandler<T extends (...args: any[]) => any>(
        existing: T | undefined,
        newHandler: T | undefined,
      ): T | undefined {
        if (!newHandler) return existing
        if (!existing) return ((...args: Parameters<T>) => {
          // Detached telemetry delivery cannot change the producer's outcome.
          try {
            const result: unknown = newHandler(...args)
            if (result instanceof Promise) return trackHandlerPromise(result.catch(() => null))
            return result
          } catch { return null }
        }) as T

        return ((...args: Parameters<T>) => {
          const existingResult = existing(...args)
          const newResult: unknown = (() => {
            try {
              const result: unknown = newHandler(...args)
              return result instanceof Promise ? trackHandlerPromise(result.catch(() => null)) : result
            } catch { return null }
          })()

          // Check if either result is a Promise and handle accordingly
          if (
            existingResult instanceof Promise ||
            newResult instanceof Promise
          ) {
            return Promise.all([
              existingResult instanceof Promise
                ? existingResult
                : Promise.resolve(existingResult),
              newResult instanceof Promise
                ? newResult
                : Promise.resolve(newResult),
            ])
          }

          return newResult
        }) as T
      }
      /* oxlint-enable typescript/no-explicit-any, typescript/no-unsafe-assignment, typescript/no-unsafe-return */

      type MergedHandlers = Required<TelStorageEventHandlers>
      const eventHandlers = resolvedHandlers.reduce(
        (acc: MergedHandlers, handler) => {
          acc.logs.onCreate = mergeHandler(
            acc.logs.onCreate,
            handler.logs?.onCreate,
          )
          acc.spans.onCreate = mergeHandler(
            acc.spans.onCreate,
            handler.spans?.onCreate,
          )
          acc.spans.onUpdate = mergeHandler(
            acc.spans.onUpdate,
            handler.spans?.onUpdate,
          )
          acc.spans.onEnd = mergeHandler(acc.spans.onEnd, handler.spans?.onEnd)
          acc.warnings.onCreate = mergeHandler(
            acc.warnings.onCreate,
            handler.warnings?.onCreate,
          )
          acc.metrics.onCreateDelta = mergeHandler(acc.metrics.onCreateDelta, handler.metrics?.onCreateDelta)
          acc.metrics.onCreateCumulative = mergeHandler(acc.metrics.onCreateCumulative, handler.metrics?.onCreateCumulative)
          acc.metrics.onCreateGauge = mergeHandler(acc.metrics.onCreateGauge, handler.metrics?.onCreateGauge)
          return acc
        },
        {
          logs: {},
          spans: {},
          traces: {},
          requests: {},
          errors: {},
          warnings: {},
          metrics: {},
        } as MergedHandlers,
      )

      handlersRegistered = true
      Object.assign(handlers, eventHandlers)

      if (allEventsBuffer.logs.create.length > 0) {
        for (const data of allEventsBuffer.logs.create) {
          eventHandlers.logs?.onCreate?.(data, immediateHandlerContext)
        }
        allEventsBuffer.logs.create = []
      }

      if (allEventsBuffer.span.create.length > 0) {
        for (const data of allEventsBuffer.span.create)
          eventHandlers.spans?.onCreate?.(data, immediateHandlerContext)
        allEventsBuffer.span.create = []
      }
      if (allEventsBuffer.span.update.length > 0) {
        for (const data of allEventsBuffer.span.update) {
          const span = data.id === undefined ? undefined : storage.spans.get(data.id)
          // Evicted by the storage cap, already reported through onDrop.
          if (!span) continue
          eventHandlers.spans?.onUpdate?.(span, data, immediateHandlerContext)
        }
        allEventsBuffer.span.update = []
      }
      if (allEventsBuffer.span.end.length > 0) {
        for (const data of allEventsBuffer.span.end) {
          const span = data.id === undefined ? undefined : storage.spans.get(data.id)
          // Evicted by the storage cap, already reported through onDrop.
          if (!span) continue
          eventHandlers.spans?.onEnd?.(span, data, immediateHandlerContext)
        }
        allEventsBuffer.span.end = []
      }

      if (allEventsBuffer.trace.end.length > 0) {
        allEventsBuffer.trace.end = []
      }

      if (allEventsBuffer.requests.end.length > 0) {
        allEventsBuffer.requests.end = []
      }

      if (allEventsBuffer.errors.create.length > 0) {
        for (const { event, revisions } of allEventsBuffer.errors.create)
          scheduleErrorDelivery(event, revisions)
        allEventsBuffer.errors.create = []
      }

      // Error prefixes must be claimed before the completed boundary. Otherwise
      // late handler registration could clean the source tree before eager
      // delivery gets its first snapshot.
      for (const boundary of boundariesAwaitingHandlerReplay.values()) {
        const event = createBoundaryEndEvent(boundary)
        const trace = event?.reconstructTrace(event.span.id)
        const pending = trace ? scheduleBoundaryDelivery(boundary, trace) : []
        if (event) {
          deferOrCleanupBoundary(boundary, event.span, pending)
        } else {
          scheduleBoundaryCleanup(boundary, pending)
        }
      }

      if (allEventsBuffer.warnings.create.length > 0) {
        for (const { event } of allEventsBuffer.warnings.create)
          void eventHandlers.warnings?.onCreate?.(
            event,
            immediateHandlerContext,
          )
        allEventsBuffer.warnings.create = []
      }

      if (allEventsBuffer.metrics.create.length > 0) {
        for (const data of allEventsBuffer.metrics.create) {
          if (!dispatchMetric(eventHandlers.metrics, data)) recordDrop('metrics')
        }
        allEventsBuffer.metrics.create = []
      }
    },
    clear() {
      storageObj.stopTimers()

      allEventsBuffer.span.create = []
      allEventsBuffer.span.update = []
      allEventsBuffer.span.end = []

      allEventsBuffer.logs.create = []

      storage.logs = []
      storage.spans.clear()
      spanRevisions.clear()
      deferredCleanupByBoundary.clear()
      for (const { cursor } of deliveryBindings) {
        cursor.spanCursor.clear()
        cursor.logCursor.clear()
      }
      activeSpanIds.clear()
      pinnedLogIds.clear()
      pinnedSpanIds.clear()
      boundariesAwaitingHandlerReplay.clear()
      finishedTraceIds.length = 0
      storage.metrics = []
      allEventsBuffer.warnings.create = []
      allEventsBuffer.trace.end = []
      allEventsBuffer.requests.end = []
      allEventsBuffer.errors.create = []
      allEventsBuffer.metrics.create = []

      structuralErrorCache.clear()
      completedRequestSnapshots.clear()

      // Reset handlers
      handlersRegistered = false
      handlers.logs = {}
      handlers.spans = {}
      handlers.traces = {}
      handlers.requests = {}
      handlers.errors = {}
      handlers.warnings = {}
      handlers.metrics = {}
    },

    _listTraces(): SpanData[] {
      // Get trace-start spans (spans where id === traceId)
      return storage.spans
        .toArray()
        .filter((span) => {
          return span.id === span.traceId
        })
        .toSorted((a, b) => decodeTime(b.id) - decodeTime(a.id))
    },

    _getTrace(traceId: string): TraceData | null {
      if (!traceId) return null

      const root = storage.spans.get(traceId)
      const traceStartSpan = root?.traceId === traceId ? root : undefined
      if (!traceStartSpan) return null

      return reconstructTrace(traceStartSpan.id, storage.spans, storage.logs, true)
    },

    _hasSpan(spanId: string): boolean {
      return storage.spans.has(spanId)
    },

    /** INV-4 test observability: total live sink-cursor entries across every sink. */
    _getSinkCursorSizes(): { spans: number; logs: number } {
      let spans = 0
      let logs = 0
      for (const cursor of sinkCursors.values()) {
        spans += cursor.spanCursor.size
        logs += cursor.logCursor.size
      }
      return { spans, logs }
    },

    _reconstructSpan(spanId: string): TraceData | null {
      const stored = reconstructTrace(spanId, storage.spans, storage.logs, false)
      if (stored) return stored
      const completed = completedRequestSnapshots.get(spanId) ?? null
      completedRequestSnapshots.delete(spanId)
      return completed
    },

    /** Concurrent request units can drain each other's metrics on multi-tenant servers. */
    _drainMetricsSince(timestamp: number): MetricPoint[] {
      const drained: MetricPoint[] = []
      storage.metrics = storage.metrics.filter((metric) => {
        if (metric.collectedAt < timestamp) return true
        drained.push(metric)
        return false
      })
      return drained
    },

    /** Storage retains batches and serializes transport sends with boundary delivery. */
    _createDeliveryBatch<T>(sink: string, send: (items: T[]) => Promise<ExportResult>, options: BatcherOptions = {}) {
      const cursor = getSinkCursor(sink)
      const items: T[] = []
      const maxBatchSize = options.maxBatchSize ?? 512
      let inFlight = 0
      let timer: ReturnType<typeof setTimeout> | undefined
      let pending: Promise<unknown> | undefined
      let closed = false
      const clearTimer = () => {
        if (timer !== undefined) clearTimeout(timer)
        timer = undefined
      }
      const flush = async (): Promise<void> => {
        clearTimer()
        if (pending) { await pending; return }
        if (items.length === 0) return
        pending = enqueueDestination(cursor, async () => {
          while (items.length > 0) {
            const batch = items.slice(0, maxBatchSize)
            inFlight = batch.length
            const result = await send(batch)
            inFlight = 0
            if (result.code === ExportResultCode.FAILED) return
            items.splice(0, batch.length)
          }
        }).finally(() => { inFlight = 0; pending = undefined }).catch(() => null)
        await pending
      }
      const batches = transportBatches.get(sink) ?? new Set()
      transportBatches.set(sink, batches)
      batches.add({ flush, close: () => { closed = true; clearTimer(); flushCallbacks.delete(flush) } })
      flushCallbacks.add(flush)
      return (item: T): void => {
        if (closed) return
        if (items.length >= (options.maxQueueSize ?? 2048)) {
          if (inFlight === items.length) return
          items.splice(inFlight, 1)
        }
        items.push(item)
        if (items.length >= maxBatchSize) void flush()
        else timer ??= setTimeout(() => { void flush() }, options.scheduledDelayMs ?? 5000)
      }
    },

    async _flushDelivery(sink: string, close = false) {
      for (const batch of transportBatches.get(sink) ?? []) {
        await batch.flush()
        if (close) batch.close()
      }
      if (close) transportBatches.delete(sink)
    },

    registerFlush(callback: () => Promise<void>) {
      flushCallbacks.add(callback)
    },

    async flush() {
      if (pendingHandlerPromises.size > 0)
        await Promise.allSettled(pendingHandlerPromises)
      const activeDeliveries = deliveryBindings.filter(
        (delivery) => delivery.queued > 0,
      )
      if (activeDeliveries.length > 0)
        await Promise.allSettled(
          activeDeliveries.map((delivery) => delivery.pending),
        )
      await Promise.all(
        Array.from(flushCallbacks).map(async (callback) => {
          try {
            await callback()
          } catch {
            // Silently catch errors in individual flush callbacks
            // to prevent one failing callback from stopping others
          }
        }),
      )
      await Promise.allSettled(pendingHandlerPromises)
      await Promise.allSettled([
        ...deliveryBindings.map((delivery) => delivery.pending),
        ...retiredDeliveries,
      ])
    },

    // Reset drop backoff after successful export
    resetDropBackoff,

    stopTimers() {
      if (!dropReportTimer) return
      clearTimeout(dropReportTimer)
      dropReportTimer = null
    },

    setPerfCallback(cb: (span: SpanData) => void) {
      perfCallback = cb
    },

    // Test helpers - internal use only
    _getInternalSpans() {
      return storage.spans.toArray()
    },
    /** Stored spans looked at so far: the constant-cost-per-span test reads it. */
    _spanStoreVisits() {
      return storage.spans.visits
    },
    _getInternalLogs() {
      return storage.logs
    },
    _getErrorTrackingConfig() {
      return { errorGroupScope, stackResolver }
    },
    _inspectDelivery() {
      return {
        bindings: deliveryBindings.map((delivery) => ({
          spanCursorSize: delivery.cursor.spanCursor.size,
          logCursorSize: delivery.cursor.logCursor.size,
        })),
      }
    },
    // Total deferred-cleanup entries across every open
    // ancestor currently being waited on.
    _getDeferredCleanupSize() {
      const ancestors = new Set([...deferredCleanupByBoundary.values()].map((entry) => entry.ancestorId))
      return { keys: ancestors.size, entries: deferredCleanupByBoundary.size }
    },
  }

  return storageObj
}

export type TelStorage = ReturnType<typeof createTelStorageCore>

// Public factory for creating storage
export function createTelStorage(opts?: {
  config?: TelStorageConfig
  handlers?: TelStorageEventHandlers[]
}): TelStorage {
  const storage = createTelStorageCore(opts?.config)
  if (opts?.handlers) {
    storage.setHandlers(...opts.handlers)
  }
  return storage
}

// Factory for null storage (zero overhead)
export function createNullStorage(): TelStorage {
  return createTelStorageCore()
}

export interface TelStorageEventHandlers {
  logs?: {
    onCreate?: (data: LogData, ctx?: HandlerContext) => unknown
  }
  spans?: Partial<{
    /** Storage coalesces live snapshots before invoking this transport batch. */
    onBatch: (spans: SpanData[]) => unknown
    onCreate: (data: SpanData, ctx?: HandlerContext) => unknown
    onUpdate: (
      span: SpanData,
      update: Partial<SpanData>,
      ctx?: HandlerContext,
    ) => unknown
    onEnd: (
      span: SpanData,
      update: Partial<SpanData>,
      ctx?: HandlerContext,
    ) => unknown
  }>
  traces?: {
    onEnd?: TraceBoundaryEndHandler
  }
  requests?: {
    onEnd?: TraceBoundaryEndHandler
  }
  errors?: {
    /**
     * Runs once for the first accepted observation of one raw error/cause chain.
     * Every failed span still retains its own `SpanData.error`; dedupe suppresses
     * only later `onCreate` calls from propagation through parent spans.
     * `originalError` is the first accepted observed value after `errorMapper`.
     */
    onCreate?: (
      event: TelErrorEvent,
      ctx?: HandlerContext,
    ) => Promise<void> | void
  }
  warnings?: {
    /** Warning level is terminal: no error export, no group mutation. */
    onCreate?: (
      event: TelErrorEvent,
      ctx?: HandlerContext,
    ) => Promise<void> | void
  }
  metrics?: {
    onCreateDelta?: (data: DeltaMetricPoint, ctx?: HandlerContext) => unknown
    onCreateCumulative?: (data: CumulativeMetricPoint, ctx?: HandlerContext) => unknown
    onCreateGauge?: (data: GaugeMetricPoint, ctx?: HandlerContext) => unknown
  }
}

export type BoundTelExporter = TelStorageEventHandlers &
  Partial<{
    /** Internal expansion for exporters with independent storage claims. */
    _bindings: BoundTelExporter[]
    /** Selects complete boundary snapshots before cursor pruning or queueing. */
    acceptsBoundary: (trace: TraceData) => boolean
    retry: RetryOptions
    flush: () => Promise<void>
    shutdown: () => Promise<void>
    /**
     * Names the destination. Bindings with equal `sink` share one
     * span-revision/log-id cursor: live handlers advance it, eager/final
     * delivery skips what it already covers. No `sink` → private cursor,
     * and complete boundaries every time.
     */
    sink: string
  }>

export interface TelExporter {
  bind(resource: TelResources, storage?: TelStorage): BoundTelExporter
}

export type TelStorageBindable = TelStorageEventHandlers | TelExporter

// Helper function to reconstruct trace data from stored spans
function reconstructTrace(
  targetSpanId: string,
  spans: SpanStore,
  logs: LogData[],
  // A boundary export (`_reconstructSpan`) reconstructs only the exported
  // span's own subtree: the span may be nested under a still-open ancestor
  // (e.g. a shared HTTP request span) that owns the trace but has not ended,
  // and that ancestor must never be captured mid-flight into a relayed
  // boundary. Full local trace reconstruction (dev UI, request cleanup)
  // still climbs to the trace root.
  climbToRoot: boolean,
): TraceData | null {
  if (!targetSpanId) return null

  const targetSpan = spans.get(targetSpanId)
  if (!targetSpan) return null

  let traceStartSpan = targetSpan
  const visited = new Set<string>() // Prevent infinite loops in case of circular references

  if (climbToRoot) {
    // An owned trace may retain a causal parent in another still-open trace.
    // Reconstruct its completed boundary without crossing that ownership root.
    while (traceStartSpan.parentId && traceStartSpan.id !== traceStartSpan.traceId && !visited.has(traceStartSpan.id)) {
      visited.add(traceStartSpan.id)
      const parentId = traceStartSpan.parentId
      const parent = spans.get(parentId)
      if (!parent) break
      traceStartSpan = parent
    }
  }

  const logsBySpan = new Map<string, LogData[]>()

  // Group logs by span/trace
  for (const log of logs) {
    const key = log.parentId ?? log.traceId ?? ''
    if (key) {
      let group = logsBySpan.get(key)
      if (!group) {
        group = []
        logsBySpan.set(key, group)
      }
      group.push(log)
    }
  }

  // Build trace tree recursively
  function buildTraceNode(span: SpanData): TraceData {
    const children = spans.children(span.id).map((s) =>
      buildTraceNode(s),
    )

    // Get logs for this span
    // For trace-start spans (id === traceId), also include trace-level logs.
    const spanLogs = [
      ...(logsBySpan.get(span.id) ?? []),
      ...(span.id === span.traceId ? (logsBySpan.get(span.traceId) ?? []) : []),
    ].filter(
      (log, index, array) =>
        // Remove duplicates based on log id
        array.findIndex((l) => l.id === log.id) === index,
    )

    return {
      ...span,
      logs: spanLogs,
      children,
    }
  }

  return buildTraceNode(traceStartSpan)
}

function pinSpanChain(
  spanId: string,
  spans: SpanStore,
  logs: LogData[],
  pinnedSpanIds: Set<string>,
  pinnedLogIds: Set<string>,
): void {
  const visited = new Set<string>()
  let current = spans.get(spanId)

  while (current && !visited.has(current.id)) {
    visited.add(current.id)
    pinnedSpanIds.add(current.id)
    for (const log of logs) {
      if (log.parentId === current.id) {
        pinnedLogIds.add(log.id)
      }
    }
    const parentId = current.parentId
    current = parentId ? spans.get(parentId) : undefined
  }
}

function createTraceReconstructor(
  boundarySpan: SpanData,
  spans: SpanStore,
  logs: LogData[],
): TraceBoundaryReconstructor {
  const cache = new Map<string, TraceData>()
  return (spanId: string) => {
    const targetSpanId = spanId || boundarySpan.id
    const cached = cache.get(targetSpanId)
    if (cached) return cached

    const trace =
      reconstructTrace(targetSpanId, spans, logs, true) ??
      reconstructTrace(boundarySpan.id, spans, logs, true) ??
      spanToTraceData(boundarySpan)
    cache.set(targetSpanId, trace)
    return trace
  }
}

function spanToTraceData(span: SpanData): TraceData {
  return {
    ...span,
    logs: [],
    children: [],
  }
}
